import { createContext, useCallback, useContext, useMemo, useRef } from "react";

import { saveFormState } from "../utils/formPersistence";

/**
 * Registry of forms that want their state persisted across an external redirect
 * (Stripe Checkout, OAuth, etc.). A form registers a `getValues` snapshotter
 * keyed by `formId` + its current `route`; the payment layer, right before it
 * redirects out of the SPA, asks the registry to snapshot the form matching the
 * current route — so pages never need to know when/whether a redirect happens.
 *
 * Restoration is intentionally NOT done here: the form re-mounts on return and
 * restores itself via `useRegisterPersistentForm`.
 */
const FormPersistenceContext = createContext(null);

/** No-op fallback so consumers (e.g. usePaymentCheckout) are safe without a provider. */
const NOOP_CONTEXT = {
  registerForm: () => () => {},
  unregisterForm: () => {},
  savePendingForRoute: () => false,
};

export const useFormPersistence = () =>
  useContext(FormPersistenceContext) || NOOP_CONTEXT;

export const FormPersistenceProvider = ({ children }) => {
  // formId -> { formId, route, getValues }. A ref (not state) — registration
  // must not trigger re-renders, and reads happen imperatively at redirect time.
  const registryRef = useRef(new Map());

  const registerForm = useCallback((config) => {
    if (!config?.formId) return () => {};
    registryRef.current.set(config.formId, config);
    return () => {
      // Only remove if this exact registration is still the active one.
      if (registryRef.current.get(config.formId) === config) {
        registryRef.current.delete(config.formId);
      }
    };
  }, []);

  const unregisterForm = useCallback((formId) => {
    registryRef.current.delete(formId);
  }, []);

  /**
   * Snapshot the registered form whose route matches `pathname` into Session
   * Storage. Called immediately before an external redirect.
   * @returns {boolean} whether a form was saved
   */
  const savePendingForRoute = useCallback((pathname) => {
    for (const config of registryRef.current.values()) {
      if (config.route !== pathname) continue;
      try {
        const data = config.getValues?.();
        return saveFormState({ formId: config.formId, route: config.route, data });
      } catch (error) {
        console.error("FormPersistence.savePendingForRoute failed:", error);
        return false;
      }
    }
    return false;
  }, []);

  const value = useMemo(
    () => ({ registerForm, unregisterForm, savePendingForRoute }),
    [registerForm, unregisterForm, savePendingForRoute],
  );

  return (
    <FormPersistenceContext.Provider value={value}>
      {children}
    </FormPersistenceContext.Provider>
  );
};

export default FormPersistenceContext;
